import { environment } from '../../environments/environment';
import type { CompanySearchApiResult } from './fetchCompanySearchResults';

export class CompanyMapperHttpError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/**
 * Direct subsidiaries of several companies in one request (`/get-companies`).
 * The endpoint returns every child in one flat list and echoes each request
 * item's `company_id` onto its children, so the parent rcid is sent as that
 * id to regroup them.
 */
export const fetchSubsidiariesOfCompanies = async (
  rcids: readonly number[],
  signal?: AbortSignal
): Promise<Map<number, CompanySearchApiResult[]>> => {
  const response = await fetch(
    `${environment.COMPANY_MAPPER_URL}/get-companies`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(
        rcids.map((rcid) => ({ rcid, company_id: String(rcid) }))
      ),
      signal,
    }
  );

  if (!response.ok) {
    throw new CompanyMapperHttpError(
      `get-companies failed: ${response.status}`,
      response.status
    );
  }

  const rows: CompanySearchApiResult[] = await response.json();
  const byParent = new Map(
    rcids.map((rcid) => [rcid, [] as CompanySearchApiResult[]])
  );
  for (const row of rows) byParent.get(Number(row.company_id))?.push(row);
  return byParent;
};
